export const formatDate = (d: Date, dateFormat?: 'default' | 'datePicker') => {
  if (d instanceof Date) {
    if (dateFormat === 'datePicker') {
      return `${d.getFullYear()}-${
        d.getMonth() + 1 < 10 ? '0' + (d.getMonth() + 1) : d.getMonth() + 1
      }-${d.getDate() < 10 ? '0' + d.getDate() : d.getDate()}`
    } else {
      return `${d.getDate() < 10 ? '0' + d.getDate() : d.getDate()}-${
        d.getMonth() + 1 < 10 ? '0' + (d.getMonth() + 1) : d.getMonth() + 1
      }-${d.getFullYear()}`
    }
  } else {
    return '00/00/0000'
  }
}

export const formatTime = (date: Date) => {
  if (date instanceof Date) {
    return date.toLocaleTimeString(undefined, {
      hour: '2-digit',
      minute: '2-digit',
    })
  }
}

export const getNth = (cycle: number) => {
  switch (cycle) {
    case 1:
      return 'first'
    case 2:
      return 'second'
    case 3:
      return 'third'
    default:
      return `${cycle}th`
  }
}

export const calculateDaysLeft = (date: number) => {
  return Math.floor((date - new Date().getTime()) / (1000 * 60 * 60 * 24))
}

// Locale-aware "20 September" / "Monday, 7 September" — the dashboard writes dates
// out in words, where formatDate's numeric dd-mm-yyyy would read as data.
export const formatLongDate = (
  date: Date,
  language: string,
  withWeekday?: boolean
) =>
  date.toLocaleDateString(language, {
    weekday: withWeekday ? 'long' : undefined,
    day: 'numeric',
    month: 'long',
  })

const MINUTE = 60 * 1000
const HOUR = 60 * MINUTE
const DAY = 24 * HOUR
const WEEK = 7 * DAY
// Calendar months vary; for "how long ago" a fixed 30 days is accurate enough and
// keeps the buckets monotonic, which a real month arithmetic would not.
const MONTH = 30 * DAY

/**
 * Dates cross the API as ISO strings while our models type them as Date, so a
 * value that "is" a Date at compile time is often a string at runtime (see how
 * Requests.tsx wraps every request date in new Date()). Coerce rather than trust.
 */
export const toDate = (value: Date | string): Date =>
  value instanceof Date ? value : new Date(value)

// "4 jan ’26": a date that stands beside a figure or names a measurement, where
// formatDate's zeros and dashes read as data. Day first in every language, as the rest
// of the app; some browsers end a short month on a full stop, which is dropped.
export const formatShortDate = (value: Date | string, language: string) => {
  const date = toDate(value)
  const month = date
    .toLocaleDateString(language, { month: 'short' })
    .replace('.', '')

  return `${date.getDate()} ${month} ’${String(date.getFullYear()).slice(-2)}`
}

/**
 * Relative age of a past date: "just now", minutes, hours, days, weeks, months,
 * and the absolute date beyond three months. Recency is what a reader actually
 * wants from a timeline; an absolute date makes them do arithmetic on every row.
 * Past three months that flips — the exact date carries more than "5 months ago".
 */
export const formatRelativeTime = (
  value: Date | string,
  t: (key: string, options?: Record<string, any>) => string
): string => {
  const date = toDate(value)
  if (isNaN(date.getTime())) {
    return ''
  }

  const elapsed = new Date().getTime() - date.getTime()

  if (elapsed < MINUTE) {
    return t('general.time.justNow')
  }
  if (elapsed < HOUR) {
    return t('general.time.minutesAgo', { count: Math.floor(elapsed / MINUTE) })
  }
  if (elapsed < DAY) {
    return t('general.time.hoursAgo', { count: Math.floor(elapsed / HOUR) })
  }
  if (elapsed < WEEK) {
    return t('general.time.daysAgo', { count: Math.floor(elapsed / DAY) })
  }
  if (elapsed < MONTH) {
    return t('general.time.weeksAgo', { count: Math.floor(elapsed / WEEK) })
  }
  if (elapsed < 3 * MONTH) {
    return t('general.time.monthsAgo', { count: Math.floor(elapsed / MONTH) })
  }

  return formatDate(date)
}

/** Which "Today / This week / Earlier" bucket a feed item falls into. */
export type TimeGroup = 'today' | 'week' | 'earlier'

export const getTimeGroup = (value: Date | string): TimeGroup => {
  const date = toDate(value)
  const startOfToday = new Date()
  startOfToday.setHours(0, 0, 0, 0)

  if (isNaN(date.getTime())) {
    return 'earlier'
  }
  if (date.getTime() >= startOfToday.getTime()) {
    return 'today'
  }
  return date.getTime() >= startOfToday.getTime() - 7 * DAY ? 'week' : 'earlier'
}
